import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  ArrowLeft, Calendar, Clock, User, Users, Camera, Video, Sun, Moon,
  Package, Image, BookOpen, Frame, RefreshCw, Save, Plus, X, Plane
} from 'lucide-react';
import { toast } from 'sonner';

const DELIVERABLE_STATUSES = [
  { value: 'pending', label: 'Pending', color: 'text-zinc-400 bg-zinc-500/10 border-zinc-500/20' },
  { value: 'in_progress', label: 'In Progress', color: 'text-blue-400 bg-blue-500/10 border-blue-500/20' },
  { value: 'ready', label: 'Ready', color: 'text-amber-400 bg-amber-500/10 border-amber-500/20' },
  { value: 'delivered', label: 'Delivered', color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20' },
];

const ProjectDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [teamMembers, setTeamMembers] = useState([]);
  const [freelancers, setFreelancers] = useState([]);

  // Deliverables state
  const [deliverables, setDeliverables] = useState({
    albums: { count: 0, sheets: 0, status: 'pending' },
    frames: { count: 0, status: 'pending' },
    magazines: { count: 0, status: 'pending' },
  });
  const [savingDeliverables, setSavingDeliverables] = useState(false);

  const isManager = (user?.roles || [user?.role]).some(r => ['admin', 'manager'].includes(r));

  // Templates
  const [templates, setTemplates] = useState([]);
  const [applyingTemplate, setApplyingTemplate] = useState(false);

  useEffect(() => { fetchProject(); fetchTeamData(); fetchTemplates(); }, [id]);

  const fetchTemplates = async () => {
    try {
      const res = await api.get('/templates');
      setTemplates(res.data);
    } catch (err) {
      console.error('fetchTemplates failed:', err);
    }
  };

  const handleApplyTemplate = async (templateId) => {
    setApplyingTemplate(true);
    try {
      const res = await api.post('/templates/apply', {
        template_id: templateId,
        project_id: id,
      });
      toast.success(res.data.message);
      fetchProject();
    } catch (err) {
      toast.error('Failed to apply template');
    } finally {
      setApplyingTemplate(false);
    }
  };

  const fetchProject = async () => {
    try {
      const res = await api.get(`/projects/${id}`);
      setProject(res.data);
      if (res.data.deliverables) {
        setDeliverables({
          albums: res.data.deliverables.albums || { count: 0, sheets: 0, status: 'pending' },
          frames: res.data.deliverables.frames || { count: 0, status: 'pending' },
          magazines: res.data.deliverables.magazines || { count: 0, status: 'pending' },
        });
      }
    } catch (err) {
      toast.error('Failed to load project');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamData = async () => {
    try {
      const [teamRes, freelancerRes] = await Promise.all([
        api.get('/users/team'),
        isManager ? api.get('/freelancers').catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      ]);
      setTeamMembers(teamRes.data);
      setFreelancers(freelancerRes.data);
    } catch (err) {
      console.error('fetchTeamData failed:', err);
    }
  };

  const handleSaveDeliverables = async () => {
    setSavingDeliverables(true);
    try {
      await api.put(`/projects/${id}/deliverables`, { deliverables });
      toast.success('Deliverables updated');
      fetchProject();
    } catch (err) {
      toast.error('Failed to update deliverables');
    } finally {
      setSavingDeliverables(false);
    }
  };

  const handleAssignTeamToEvent = async (eventIdx, members, eventFreelancers) => {
    try {
      await api.put(`/projects/${id}/team-assignments`, {
        event_index: eventIdx,
        members: members,
        freelancers: eventFreelancers,
      });
      toast.success('Team assigned');
      fetchProject();
    } catch (err) {
      toast.error('Failed to assign team');
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F5A623]"></div>
      </div>
    );
  }

  if (!project) return null;

  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'team', label: 'Team Alignment' },
    { key: 'deliverables', label: 'Deliverables' },
    { key: 'workflow', label: 'Workflow' },
  ];

  return (
    <div className="space-y-6" data-testid="project-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/projects')} className="text-zinc-400 hover:text-white" data-testid="back-to-projects">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold text-white tracking-tight">{project.title}</h1>
            <Badge variant="outline" className={`text-xs ${(project.brand || 'wittyshadows') === 'triobliss' ? 'bg-pink-500/10 text-pink-400 border-pink-500/20' : 'bg-[#F5A623]/10 text-[#F5A623] border-[#F5A623]/20'}`}>
              {(project.brand || 'wittyshadows') === 'triobliss' ? 'Trio Bliss' : 'Wittyshadows'}
            </Badge>
          </div>
          <p className="text-zinc-500 text-sm mt-0.5">{project.client_name} | {project.event_type}</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchProject} className="border-[#F5A623]/30 text-[#F5A623] hover:bg-[#F5A623]/10">
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-white/10 pb-0">
        {tabs.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === tab.key ? 'text-[#F5A623] border-[#F5A623]' : 'text-zinc-500 border-transparent hover:text-white'
            }`} data-testid={`tab-${tab.key}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card className="bg-[#121212] border-white/10">
                <CardContent className="p-3">
                  <p className="text-[10px] text-zinc-500 uppercase">Status</p>
                  <Badge variant="outline" className="mt-1 text-xs capitalize">{project.status?.replace(/_/g, ' ')}</Badge>
                </CardContent>
              </Card>
              <Card className="bg-[#121212] border-white/10">
                <CardContent className="p-3">
                  <p className="text-[10px] text-zinc-500 uppercase">Amount</p>
                  <p className="text-white font-semibold mt-1">&#8377;{(project.total_amount || 0).toLocaleString('en-IN')}</p>
                </CardContent>
              </Card>
              <Card className="bg-[#121212] border-white/10">
                <CardContent className="p-3">
                  <p className="text-[10px] text-zinc-500 uppercase">Shoot Date</p>
                  <p className="text-white text-sm mt-1">{formatDate(project.shoot_date)}</p>
                </CardContent>
              </Card>
              <Card className="bg-[#121212] border-white/10">
                <CardContent className="p-3">
                  <p className="text-[10px] text-zinc-500 uppercase">Deadline</p>
                  <p className={`text-sm mt-1 ${project.days_remaining < 0 ? 'text-rose-400' : 'text-white'}`}>{formatDate(project.delivery_deadline)}</p>
                </CardContent>
              </Card>
            </div>

            {/* Progress */}
            <Card className="bg-[#121212] border-white/10">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-zinc-400">Progress</span>
                  <span className="text-sm text-white">{project.progress_percent}%</span>
                </div>
                <Progress value={project.progress_percent} className="h-2 bg-white/5" />
                <div className="flex justify-between mt-2 text-xs text-zinc-500">
                  <span>{project.days_elapsed}d elapsed</span>
                  <span>{project.days_remaining > 0 ? `${project.days_remaining}d left` : 'Overdue'}</span>
                </div>
              </CardContent>
            </Card>

            {/* Events */}
            {(project.events || []).length > 0 && (
              <Card className="bg-[#121212] border-white/10">
                <CardContent className="p-4">
                  <h3 className="text-white font-medium mb-3">Events</h3>
                  <div className="space-y-3">
                    {project.events.map((ev, i) => (
                      <div key={ev.id || i} className="p-3 bg-[#0A0A0A] rounded-lg border border-white/5">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            {ev.timing === 'night' ? <Moon className="h-4 w-4 text-indigo-400" /> : <Sun className="h-4 w-4 text-amber-400" />}
                            <div>
                              <p className="text-white text-sm font-medium">{ev.event_name || 'Event'}</p>
                              <p className="text-xs text-zinc-500">{formatDate(ev.date)} | {ev.timing} shoot</p>
                            </div>
                          </div>
                          <div className="flex gap-1 flex-wrap justify-end">
                            {(ev.team || []).map(t => (
                              <Badge key={t.user_id} variant="outline" className="text-[10px] bg-emerald-500/10 text-emerald-400 border-emerald-500/20">{t.name}</Badge>
                            ))}
                            {(ev.freelancers || []).map(f => (
                              <Badge key={f.user_id || f.freelancer_id} variant="outline" className="text-[10px] bg-amber-500/10 text-amber-400 border-amber-500/20">{f.name}</Badge>
                            ))}
                          </div>
                        </div>
                        {/* Crew per event */}
                        <div className="flex items-center gap-4 text-xs text-zinc-400 pt-2 border-t border-white/5">
                          <span className="flex items-center gap-1"><Camera className="h-3 w-3" /> {ev.num_photographers || 0} Photographers</span>
                          <span className="flex items-center gap-1"><Video className="h-3 w-3" /> {ev.num_videographers || 0} Videographers</span>
                          <span className="flex items-center gap-1"><Plane className="h-3 w-3" /> Drone: {ev.drone ? 'Yes' : 'No'}</span>
                          {ev.special_requirements && <span className="text-zinc-500">| {ev.special_requirements}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Notes */}
            {project.notes && (
              <Card className="bg-[#121212] border-white/10">
                <CardContent className="p-4">
                  <h3 className="text-white font-medium mb-2">Notes</h3>
                  <p className="text-sm text-zinc-400">{project.notes}</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Side Info */}
          <div className="space-y-4">
            <Card className="bg-[#121212] border-white/10">
              <CardContent className="p-4 space-y-3">
                <h3 className="text-white font-medium">Crew Summary</h3>
                {(project.events || []).length === 0 ? (
                  <p className="text-sm text-zinc-500">No events added yet</p>
                ) : (
                  <div className="space-y-2">
                    {(project.events || []).map((ev, i) => (
                      <div key={i} className="p-2 bg-[#0A0A0A] rounded border border-white/5">
                        <p className="text-xs text-white font-medium mb-1">{ev.event_name || `Event ${i+1}`}</p>
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-zinc-400">
                          <span><Camera className="h-2.5 w-2.5 inline mr-0.5" />{ev.num_photographers || 0}P</span>
                          <span><Video className="h-2.5 w-2.5 inline mr-0.5" />{ev.num_videographers || 0}V</span>
                          {ev.drone && <span className="text-emerald-400"><Plane className="h-2.5 w-2.5 inline mr-0.5" />Drone</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="bg-[#121212] border-white/10">
              <CardContent className="p-4 space-y-2">
                <h3 className="text-white font-medium mb-1">Assigned To</h3>
                {project.assigned_to_name ? (
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-full bg-[#F5A623]/20 flex items-center justify-center">
                      <User className="h-4 w-4 text-[#F5A623]" />
                    </div>
                    <span className="text-white text-sm">{project.assigned_to_name}</span>
                  </div>
                ) : (
                  <p className="text-sm text-zinc-500">Not assigned</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Team Alignment Tab */}
      {activeTab === 'team' && (
        <TeamAlignmentTab project={project} teamMembers={teamMembers} freelancers={freelancers}
          isManager={isManager} onAssign={handleAssignTeamToEvent} onRefresh={fetchProject} />
      )}

      {/* Deliverables Tab */}
      {activeTab === 'deliverables' && (
        <div className="max-w-2xl space-y-6" data-testid="deliverables-tab">
          <div className="space-y-4">
            {/* Albums */}
            <Card className="bg-[#121212] border-white/10">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <BookOpen className="h-5 w-5 text-[#F5A623]" />
                  <h3 className="text-white font-medium">Albums</h3>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-zinc-400 text-xs">No. of Albums</Label>
                    <Input type="number" value={deliverables.albums.count} onChange={e => setDeliverables(d => ({...d, albums: {...d.albums, count: parseInt(e.target.value) || 0}}))}
                      className="bg-[#0A0A0A] border-white/10 text-white mt-1" disabled={!isManager} data-testid="albums-count" />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Sheets per Album</Label>
                    <Input type="number" value={deliverables.albums.sheets} onChange={e => setDeliverables(d => ({...d, albums: {...d.albums, sheets: parseInt(e.target.value) || 0}}))}
                      className="bg-[#0A0A0A] border-white/10 text-white mt-1" disabled={!isManager} data-testid="albums-sheets" />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Status</Label>
                    <Select value={deliverables.albums.status} onValueChange={v => setDeliverables(d => ({...d, albums: {...d.albums, status: v}}))} disabled={!isManager}>
                      <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white mt-1" data-testid="albums-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-white/10">
                        {DELIVERABLE_STATUSES.map(s => (
                          <SelectItem key={s.value} value={s.value} className="text-white hover:bg-white/10">{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Frames */}
            <Card className="bg-[#121212] border-white/10">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Frame className="h-5 w-5 text-[#F5A623]" />
                  <h3 className="text-white font-medium">Frames</h3>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-400 text-xs">No. of Frames</Label>
                    <Input type="number" value={deliverables.frames.count} onChange={e => setDeliverables(d => ({...d, frames: {...d.frames, count: parseInt(e.target.value) || 0}}))}
                      className="bg-[#0A0A0A] border-white/10 text-white mt-1" disabled={!isManager} data-testid="frames-count" />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Status</Label>
                    <Select value={deliverables.frames.status} onValueChange={v => setDeliverables(d => ({...d, frames: {...d.frames, status: v}}))} disabled={!isManager}>
                      <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white mt-1" data-testid="frames-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-white/10">
                        {DELIVERABLE_STATUSES.map(s => (
                          <SelectItem key={s.value} value={s.value} className="text-white hover:bg-white/10">{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Magazines */}
            <Card className="bg-[#121212] border-white/10">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Image className="h-5 w-5 text-[#F5A623]" />
                  <h3 className="text-white font-medium">Magazines</h3>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-400 text-xs">No. of Magazines</Label>
                    <Input type="number" value={deliverables.magazines.count} onChange={e => setDeliverables(d => ({...d, magazines: {...d.magazines, count: parseInt(e.target.value) || 0}}))}
                      className="bg-[#0A0A0A] border-white/10 text-white mt-1" disabled={!isManager} data-testid="magazines-count" />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Status</Label>
                    <Select value={deliverables.magazines.status} onValueChange={v => setDeliverables(d => ({...d, magazines: {...d.magazines, status: v}}))} disabled={!isManager}>
                      <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white mt-1" data-testid="magazines-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-white/10">
                        {DELIVERABLE_STATUSES.map(s => (
                          <SelectItem key={s.value} value={s.value} className="text-white hover:bg-white/10">{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {isManager && (
            <Button onClick={handleSaveDeliverables} disabled={savingDeliverables} className="bg-[#F5A623] hover:bg-[#F5A623]/90 text-black" data-testid="save-deliverables-btn">
              <Save className="h-4 w-4 mr-2" /> {savingDeliverables ? 'Saving...' : 'Save Deliverables'}
            </Button>
          )}
        </div>
      )}

      {/* Workflow Tab */}
      {activeTab === 'workflow' && (
        <div className="space-y-6" data-testid="workflow-tab">
          <div>
            <h3 className="text-white font-medium mb-1">Apply Workflow Template</h3>
            <p className="text-sm text-zinc-500 mb-4">One-click to create all standard tasks for this project</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {templates.map(t => (
              <Card key={t.id} className="bg-[#121212] border-white/10 hover:border-white/20 transition-colors">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-white font-medium">{t.name}</h4>
                    {t.is_custom && <Badge variant="outline" className="text-[10px] bg-purple-500/10 text-purple-400 border-purple-500/20">Custom</Badge>}
                  </div>
                  <p className="text-xs text-zinc-500 mb-3">{t.description}</p>
                  <div className="flex flex-wrap gap-1 mb-3">
                    {t.tasks.map((task, i) => (
                      <Badge key={i} variant="outline" className="text-[10px] bg-white/5 text-zinc-400 border-white/10">
                        {task.title} ({task.days_offset}d)
                      </Badge>
                    ))}
                  </div>
                  {isManager && (
                    <Button size="sm" onClick={() => handleApplyTemplate(t.id)} disabled={applyingTemplate}
                      className="bg-[#F5A623] hover:bg-[#F5A623]/90 text-black text-xs w-full" data-testid={`apply-template-${t.id}`}>
                      {applyingTemplate ? 'Applying...' : `Apply (${t.tasks.length} tasks)`}
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const TeamAlignmentTab = ({ project, teamMembers, freelancers, isManager, onAssign, onRefresh }) => {
  const events = project.events || [];
  const [editingEvent, setEditingEvent] = useState(null);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [selectedFreelancers, setSelectedFreelancers] = useState([]);

  // Add Event inline form
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [newEvent, setNewEvent] = useState({
    event_name: '', date: '', timing: 'day',
    num_photographers: 1, num_videographers: 0, drone: false, special_requirements: '',
  });
  const [addingEvent, setAddingEvent] = useState(false);

  const handleAddEvent = async () => {
    if (!newEvent.event_name.trim()) { toast.error('Enter event name'); return; }
    if (!newEvent.date) { toast.error('Pick event date'); return; }
    setAddingEvent(true);
    try {
      await api.post(`/projects/${project.id}/events`, {
        event_name: newEvent.event_name.trim(),
        date: new Date(newEvent.date).toISOString(),
        timing: newEvent.timing,
        num_photographers: parseInt(newEvent.num_photographers) || 0,
        num_videographers: parseInt(newEvent.num_videographers) || 0,
        drone: newEvent.drone,
        special_requirements: newEvent.special_requirements,
        team: [],
        freelancers: [],
      });
      toast.success('Event added');
      setNewEvent({ event_name: '', date: '', timing: 'day', num_photographers: 1, num_videographers: 0, drone: false, special_requirements: '' });
      setShowAddEvent(false);
      onRefresh && onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add event');
    } finally {
      setAddingEvent(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!confirm('Remove this event from the project?')) return;
    try {
      await api.delete(`/projects/${project.id}/events/${eventId}`);
      toast.success('Event removed');
      onRefresh && onRefresh();
    } catch (err) {
      toast.error('Failed to remove event');
    }
  };

  const startEdit = (idx) => {
    const ev = events[idx];
    setEditingEvent(idx);
    setSelectedMembers(ev.team || []);
    setSelectedFreelancers(ev.freelancers || []);
  };

  const saveTeam = async (idx) => {
    await onAssign(idx, selectedMembers, selectedFreelancers);
    setEditingEvent(null);
  };

  const addMember = (member) => {
    if (!selectedMembers.find(m => m.user_id === member.id)) {
      setSelectedMembers([...selectedMembers, { user_id: member.id, name: member.name }]);
    }
  };

  const removeMember = (userId) => {
    setSelectedMembers(selectedMembers.filter(m => m.user_id !== userId));
  };

  const addFreelancer = (f) => {
    if (!selectedFreelancers.find(x => x.user_id === f.id)) {
      setSelectedFreelancers([...selectedFreelancers, { user_id: f.id, name: f.name }]);
    }
  };

  const removeFreelancer = (userId) => {
    setSelectedFreelancers(selectedFreelancers.filter(f => f.user_id !== userId));
  };

  // Helper rendering for Add Event form
  const renderAddEventForm = () => (
    <Card className="bg-[#0F0F0F] border-[#F5A623]/30" data-testid="add-event-form">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-[#F5A623] text-sm font-semibold">Add Event</h3>
          <button onClick={() => setShowAddEvent(false)} className="text-zinc-500 hover:text-white">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-zinc-400 text-[11px]">Event Name *</Label>
            <Input value={newEvent.event_name}
              onChange={e => setNewEvent({...newEvent, event_name: e.target.value})}
              className="bg-[#0A0A0A] border-white/10 text-white mt-1 text-sm"
              placeholder="Haldi / Mehendi / Wedding" data-testid="new-event-name" />
          </div>
          <div>
            <Label className="text-zinc-400 text-[11px]">Date *</Label>
            <Input type="date" value={newEvent.date}
              onChange={e => setNewEvent({...newEvent, date: e.target.value})}
              className="bg-[#0A0A0A] border-white/10 text-white mt-1 text-sm" data-testid="new-event-date" />
          </div>
          <div>
            <Label className="text-zinc-400 text-[11px]">Timing</Label>
            <div className="flex gap-1.5 mt-1">
              {['day', 'night'].map(t => (
                <button key={t} type="button"
                  onClick={() => setNewEvent({...newEvent, timing: t})}
                  className={`flex-1 px-3 py-1.5 rounded border text-xs capitalize ${
                    newEvent.timing === t ? 'border-[#F5A623] bg-[#F5A623]/10 text-[#F5A623]' : 'border-white/10 text-zinc-400'
                  }`}>{t} shoot</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-zinc-400 text-[11px]">Photos</Label>
              <Input type="number" min="0" value={newEvent.num_photographers}
                onChange={e => setNewEvent({...newEvent, num_photographers: e.target.value})}
                className="bg-[#0A0A0A] border-white/10 text-white mt-1 text-sm" />
            </div>
            <div>
              <Label className="text-zinc-400 text-[11px]">Video</Label>
              <Input type="number" min="0" value={newEvent.num_videographers}
                onChange={e => setNewEvent({...newEvent, num_videographers: e.target.value})}
                className="bg-[#0A0A0A] border-white/10 text-white mt-1 text-sm" />
            </div>
            <div>
              <Label className="text-zinc-400 text-[11px]">Drone</Label>
              <button type="button"
                onClick={() => setNewEvent({...newEvent, drone: !newEvent.drone})}
                className={`w-full mt-1 px-2 py-1.5 rounded border text-xs ${
                  newEvent.drone ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400' : 'border-white/10 text-zinc-400'
                }`}>{newEvent.drone ? 'Yes' : 'No'}</button>
            </div>
          </div>
        </div>
        <div>
          <Label className="text-zinc-400 text-[11px]">Special Requirements</Label>
          <Input value={newEvent.special_requirements}
            onChange={e => setNewEvent({...newEvent, special_requirements: e.target.value})}
            className="bg-[#0A0A0A] border-white/10 text-white mt-1 text-sm"
            placeholder="e.g. LED screen coverage" />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" size="sm" onClick={() => setShowAddEvent(false)}
            className="border-white/10 text-white hover:bg-white/5">Cancel</Button>
          <Button size="sm" onClick={handleAddEvent} disabled={addingEvent}
            className="bg-[#F5A623] hover:bg-[#F5A623]/90 text-black" data-testid="submit-new-event">
            {addingEvent ? 'Adding...' : 'Add Event'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  if (events.length === 0) {
    return (
      <div className="space-y-4" data-testid="team-tab-empty">
        {!showAddEvent ? (
          <div className="text-center py-12 text-zinc-500">
            <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No events added to this project yet.</p>
            <p className="text-sm mt-1 mb-4">Add an event first, then assign team members to it.</p>
            {isManager && (
              <Button onClick={() => setShowAddEvent(true)}
                className="bg-[#F5A623] hover:bg-[#F5A623]/90 text-black"
                data-testid="add-first-event-btn">
                <Plus className="h-4 w-4 mr-1.5" /> Add First Event
              </Button>
            )}
          </div>
        ) : (
          renderAddEventForm()
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="team-alignment-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-zinc-400">Assign team members and freelancers to each event. This team will also be synced with Data Management.</p>
        {isManager && !showAddEvent && (
          <Button size="sm" onClick={() => setShowAddEvent(true)}
            className="bg-[#F5A623] hover:bg-[#F5A623]/90 text-black" data-testid="add-event-btn">
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Add Event
          </Button>
        )}
      </div>

      {showAddEvent && renderAddEventForm()}

      {events.map((ev, idx) => (
        <Card key={ev.id || idx} className="bg-[#121212] border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                {ev.timing === 'night' ? <Moon className="h-5 w-5 text-indigo-400" /> : <Sun className="h-5 w-5 text-amber-400" />}
                <div>
                  <h3 className="text-white font-medium">{ev.event_name || `Event ${idx + 1}`}</h3>
                  <p className="text-xs text-zinc-500">{ev.date ? new Date(ev.date).toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' }) : '—'} | {ev.timing} shoot</p>
                  <div className="flex gap-3 mt-0.5 text-[10px] text-zinc-500">
                    <span>{ev.num_photographers || 0} Photographers</span>
                    <span>{ev.num_videographers || 0} Videographers</span>
                    {ev.drone && <span className="text-emerald-400">Drone</span>}
                    {ev.special_requirements && <span>| {ev.special_requirements}</span>}
                  </div>
                </div>
              </div>
              {isManager && editingEvent !== idx && (
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => startEdit(idx)}
                    className="border-[#F5A623]/30 text-[#F5A623] hover:bg-[#F5A623]/10 text-xs" data-testid={`edit-team-${idx}`}>
                    <Users className="h-3 w-3 mr-1" /> Edit Team
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleDeleteEvent(ev.id)}
                    className="border-rose-500/30 text-rose-400 hover:bg-rose-500/10 text-xs"
                    data-testid={`delete-event-${idx}`}>
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              )}
            </div>

            {/* Current Team */}
            {editingEvent !== idx && (
              <div className="flex flex-wrap gap-2">
                {(ev.team || []).length === 0 && (ev.freelancers || []).length === 0 && (
                  <span className="text-xs text-zinc-500">No team assigned yet</span>
                )}
                {(ev.team || []).map(t => (
                  <Badge key={t.user_id} variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                    <User className="h-3 w-3 mr-1" /> {t.name}
                  </Badge>
                ))}
                {(ev.freelancers || []).map(f => (
                  <Badge key={f.user_id || f.freelancer_id} variant="outline" className="bg-amber-500/10 text-amber-400 border-amber-500/20">
                    <User className="h-3 w-3 mr-1" /> {f.name} (Freelancer)
                  </Badge>
                ))}
              </div>
            )}

            {/* Editing */}
            {editingEvent === idx && (
              <div className="space-y-3 pt-3 border-t border-white/5 mt-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Team Members */}
                  <div>
                    <Label className="text-zinc-400 text-xs uppercase tracking-wider mb-2 block">Team Members</Label>
                    {/* Selected */}
                    <div className="flex flex-wrap gap-1 mb-2 min-h-[28px]">
                      {selectedMembers.length === 0 && <span className="text-[11px] text-zinc-600">None selected</span>}
                      {selectedMembers.map(m => (
                        <Badge key={m.user_id} variant="outline" className="text-xs bg-emerald-500/10 text-emerald-400 border-emerald-500/20 cursor-pointer hover:bg-emerald-500/20" onClick={() => removeMember(m.user_id)}>
                          {m.name} <X className="h-3 w-3 ml-1" />
                        </Badge>
                      ))}
                    </div>
                    {/* Available to add */}
                    <div className="max-h-40 overflow-y-auto space-y-1 bg-[#0A0A0A] rounded-md border border-white/10 p-2">
                      {teamMembers.filter(m => !selectedMembers.find(s => s.user_id === m.id)).length === 0 ? (
                        <p className="text-[11px] text-zinc-600 text-center py-2">All members assigned</p>
                      ) : (
                        teamMembers.filter(m => !selectedMembers.find(s => s.user_id === m.id)).map(m => (
                          <button key={m.id} onClick={() => addMember(m)} type="button"
                            className="w-full flex items-center gap-2 px-2 py-1.5 text-left text-xs text-zinc-300 hover:bg-white/10 rounded transition-colors"
                            data-testid={`add-member-${m.id}`}>
                            <Plus className="h-3 w-3 text-emerald-400 shrink-0" />
                            <span>{m.name}</span>
                            <span className="text-[10px] text-zinc-600 ml-auto capitalize">{(m.roles || [m.role]).join(', ').replace(/_/g, ' ')}</span>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                  {/* Freelancers */}
                  <div>
                    <Label className="text-zinc-400 text-xs uppercase tracking-wider mb-2 block">Freelancers</Label>
                    {/* Selected */}
                    <div className="flex flex-wrap gap-1 mb-2 min-h-[28px]">
                      {selectedFreelancers.length === 0 && <span className="text-[11px] text-zinc-600">None selected</span>}
                      {selectedFreelancers.map(f => (
                        <Badge key={f.user_id} variant="outline" className="text-xs bg-amber-500/10 text-amber-400 border-amber-500/20 cursor-pointer hover:bg-amber-500/20" onClick={() => removeFreelancer(f.user_id)}>
                          {f.name} <X className="h-3 w-3 ml-1" />
                        </Badge>
                      ))}
                    </div>
                    {/* Available to add */}
                    <div className="max-h-40 overflow-y-auto space-y-1 bg-[#0A0A0A] rounded-md border border-white/10 p-2">
                      {freelancers.filter(f => !selectedFreelancers.find(s => s.user_id === f.id)).length === 0 ? (
                        <p className="text-[11px] text-zinc-600 text-center py-2">{freelancers.length === 0 ? 'No freelancers available' : 'All freelancers assigned'}</p>
                      ) : (
                        freelancers.filter(f => !selectedFreelancers.find(s => s.user_id === f.id)).map(f => (
                          <button key={f.id} onClick={() => addFreelancer(f)} type="button"
                            className="w-full flex items-center gap-2 px-2 py-1.5 text-left text-xs text-zinc-300 hover:bg-white/10 rounded transition-colors"
                            data-testid={`add-freelancer-${f.id}`}>
                            <Plus className="h-3 w-3 text-amber-400 shrink-0" />
                            <span>{f.name}</span>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" size="sm" onClick={() => setEditingEvent(null)} className="border-white/10 text-zinc-400 text-xs">Cancel</Button>
                  <Button size="sm" onClick={() => saveTeam(idx)} className="bg-[#F5A623] hover:bg-[#F5A623]/90 text-black text-xs" data-testid={`save-team-${idx}`}>
                    <Save className="h-3 w-3 mr-1" /> Save Team
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default ProjectDetailPage;
